/**
 * 圖片壓縮工具
 * 壓縮上傳圖片尺寸（長邊預設 1280px、品質 0.92），降低 canvas 負擔讓角點拖曳更順暢，
 * 並讓不同解析度的原圖在 canvas 上的角點視覺尺寸一致。
 *
 * 只縮不放：原圖長邊 ≤ 上限就直接回傳原檔。
 */
export const compressImageForUpload = (
  file: File,
  maxLongSide = 1280,
  quality = 0.92
): Promise<{ file: File; previewUrl: string }> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const longSide = Math.max(width, height);

      if (longSide <= maxLongSide) {
        resolve({ file, previewUrl: URL.createObjectURL(file) });
        return;
      }

      const ratio = maxLongSide / longSide;
      const newW = Math.round(width * ratio);
      const newH = Math.round(height * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve({ file, previewUrl: URL.createObjectURL(file) });
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file, previewUrl: URL.createObjectURL(file) });
            return;
          }
          const compressedFile = new File([blob], file.name, { type: "image/jpeg" });
          resolve({ file: compressedFile, previewUrl: URL.createObjectURL(compressedFile) });
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ file, previewUrl: URL.createObjectURL(file) });
    };
    img.src = url;
  });
};

/** 主動解碼驗證的逾時保底（毫秒）：逾時視為通過（fail-open），別讓上傳流程或旋轉視窗卡死。 */
export const IMAGE_DECODE_CHECK_TIMEOUT_MS = 10000;

/**
 * JPEG：照區段結構走到「主影像」的 EOI（FFD9）。
 *
 * - 帶長度的區段（APPn、DQT、SOF、DHT…）照長度整段跳過 —— EXIF 內嵌縮圖自帶的
 *   FFD9 在 APP1 裡面，不會被當成主影像的結束。
 * - SOS 之後是壓縮資料：資料裡的 FF 接 00（填充）、D0–D7（RST）或 01–BF 都當資料，
 *   遇到 C0 以上的其他 FFxx 才是下一個標記。01–BF 只在壓縮資料損壞時出現，libjpeg 在
 *   restart 邊界會丟掉、往後重找，照樣解得出來 —— 當成標記讀的話會拿資料當長度亂跳、
 *   把解得出來的檔判成截斷。漸進式 JPEG 有多段 SOS，照同樣規則一段段走。
 * - 遇到 EOI 就是完整，之後還有什麼都不管（見 hasCompleteImageStructure 的說明）。
 * - 標記之間夾了多餘的 bytes：比照 libjpeg 的 next_marker 丟掉、往後找下一個 FF
 *   （唯一不同是標記位置的 FF00：libjpeg 吞掉繼續找，這裡直接放行，見下）。
 *   解碼器對這種檔只警告、照樣解得出來；直接放行的話，它們被截斷時會漏擋，
 *   後段被填成 0 的損壞檔也一樣（kanban 1003071 code review）。
 *
 * 回傳 false 只有一種情況：還沒遇到 EOI 就到了檔尾（截斷）。
 * 只有兩種看不懂的情況回 true、交給 decode 那層：標記位置是 FF00、區段長度小於 2。
 * 保留標記、重複的 SOI 照帶長度的區段讀，解碼器對它們本來就會報錯。
 */
const jpegReachesEndOfImage = (bytes: Uint8Array): boolean => {
  const n = bytes.length;
  let p = 2; // 跳過 SOI
  for (;;) {
    if (p >= n) return false;
    if (bytes[p] !== 0xff) {
      p = bytes.indexOf(0xff, p);
      if (p < 0) return false;
    }
    while (p < n && bytes[p] === 0xff) p++; // 標記前可以有多個 FF 填充
    if (p >= n) return false;
    const marker = bytes[p++];
    if (marker === 0xd9) return true;
    if (marker === 0x00) return true;
    if ((marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) continue; // 無長度的標記
    if (p + 2 > n) return false;
    const length = (bytes[p] << 8) | bytes[p + 1];
    if (length < 2) return true;
    p += length;
    if (p > n) return false;
    if (marker !== 0xda) continue;
    for (;;) {
      p = bytes.indexOf(0xff, p);
      if (p < 0 || p + 1 >= n) return false;
      const next = bytes[p + 1];
      if (next < 0xc0 || (next >= 0xd0 && next <= 0xd7)) {
        p += 2;
      } else if (next === 0xff) {
        p += 1;
      } else {
        break;
      }
    }
  }
};

/**
 * PNG：照 chunk 結構（長度 4 + 型別 4 + 資料 + CRC 4）走到 IEND。
 * chunk 超出檔尾就是截斷；型別不是四個英文字母代表結構看不懂，回 true。
 */
const pngReachesEnd = (bytes: Uint8Array): boolean => {
  const n = bytes.length;
  let p = 8; // 跳過 8-byte 簽名
  for (;;) {
    if (p + 8 > n) return false;
    const length =
      bytes[p] * 0x1000000 + ((bytes[p + 1] << 16) | (bytes[p + 2] << 8) | bytes[p + 3]);
    for (let i = p + 4; i < p + 8; i++) {
      const c = bytes[i];
      if (!((c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a))) return true;
    }
    const chunkEnd = p + 12 + length;
    if (chunkEnd > n) return false;
    if (bytes[p + 4] === 0x49 && bytes[p + 5] === 0x45 && bytes[p + 6] === 0x4e && bytes[p + 7] === 0x44) {
      return true; // IEND
    }
    p = chunkEnd;
  }
};

/**
 * 檔案位元組層級的結構完整性檢查（kanban 113663 QA 三修；kanban 1003071 改為照結構走）。
 *
 * Chrome 實測事實（QA 站複驗）：img.decode() 對「檔頭完整、資料截斷」的 JPEG
 * 也會 resolve（naturalWidth 是檔頭寫的完整尺寸），decode()/onerror 只對完全
 * 無法解析的垃圾檔有效 —— 所以攔截截斷檔不能依賴瀏覽器解碼行為，只能靠
 * 檔案格式層級的事實：照 JPEG 區段 / PNG chunk 結構走，走到檔尾還沒遇到主影像的
 * EOI / IEND，就是截斷。
 *
 * 為什麼不是「看檔尾有沒有結束標記」（kanban 1003071）：舊版只在檔尾 64 bytes 內找
 * FFD9 / IEND，但格式允許結束標記之後還有資料，手機相機原檔常見 ——
 * 三星在主影像之後附 SEF 資料段、動態相片接一段影片、HDR 照片接第二張影像。
 * 這些完整的照片被判成截斷檔，學生選圖當下就看到「圖片無法讀取」、作文送不出去。
 * 主影像之後的資料瀏覽器本來就不讀，這裡也不管。
 *
 * fail-open 契約：只有「認得的格式 + 走到檔尾還沒結束」才判壞 ——
 * - 不認得的格式（GIF/WebP/HEIC…）一律放行，不擅自判壞
 * - 檔頭不符任何已知格式（垃圾檔）也放行，交給既有 onerror/decode 路徑（那條對垃圾檔有效）
 * - 結構看不懂（JPEG 標記位置是 FF00、區段長度小於 2；PNG chunk 型別不是英文字母）放行，同上
 * - 空檔、超短檔（< 4 bytes）放行不炸
 *
 * 純函式、不碰 DOM，可用真實 bytes 單獨測試。
 */
export const hasCompleteImageStructure = (bytes: Uint8Array): boolean => {
  if (!bytes || bytes.length < 4) return true;
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    return jpegReachesEndOfImage(bytes);
  }
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return pngReachesEnd(bytes);
  }
  return true;
};

/**
 * 主動解碼驗證（kanban 113663 QA 追修）。
 *
 * 定位（QA 三修後修正認知）：Chrome 實測證實 decode() 對「檔頭完整、資料截斷」
 * 的 JPEG 也會 resolve —— 這層「攔不到截斷檔」，截斷檔靠 hasCompleteImageStructure
 * 的位元組結構檢查。這層留著攔「完全無法解析的垃圾檔」（decode 對它會 reject），
 * 兩層任一判壞就擋。
 *
 * fail-open 契約：只有 decode() 明確 reject 才判壞檔；「驗證本身做不了」一律當通過 ——
 * - 沒有 decode（舊瀏覽器）→ 跳過驗證維持現狀
 * - decode 同步炸掉（API 異常）→ 通過
 * - 逾時（timeoutMs > 0 時）→ 通過，別讓呼叫端的 await 永遠不 settle
 *
 * 只吃鴨子型別（有沒有 decode 而已），不綁 DOM 的 HTMLImageElement，可單獨測試。
 */
export const verifyImageDecodable = (
  img: { decode?: () => Promise<void> },
  timeoutMs?: number
): Promise<boolean> => {
  if (typeof img.decode !== "function") return Promise.resolve(true);
  let decoding: Promise<boolean>;
  try {
    decoding = img.decode().then(
      () => true,
      () => false
    );
  } catch {
    return Promise.resolve(true);
  }
  if (!timeoutMs || timeoutMs <= 0) return decoding;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(true), timeoutMs);
  });
  return Promise.race([decoding, timeout]).then((ok) => {
    if (timer !== undefined) clearTimeout(timer);
    return ok;
  });
};

/**
 * 上傳入圖階段的壞檔驗證，兩層擋、任一判壞就擋（kanban 113663 QA 三修）。
 *
 * 第一層：位元組結構完整性（hasCompleteImageStructure）—— 截斷的 JPEG/PNG 照結構走
 * 到檔尾都遇不到主影像的結束標記，這是檔案格式層級的事實，與瀏覽器解碼寬容度無關。QA 複驗證實
 * 只靠 decode() 攔不到「檔頭完整、資料截斷」的檔（decode 照樣 resolve）。
 * 第二層：既有的 img.decode() 驗證 —— 對完全無法解析的垃圾檔有效。
 *
 * 在 handleImageChange 把檔案放進 processedGroups「之前」呼叫 —— 驗掉的壞檔
 * 不佔格、不開旋轉視窗、更不可能被交卷送出。
 *
 * fail-open 契約不變：讀檔失敗、格式不認得、環境不支援（沒有 Image / objectURL /
 * decode）、逾時 → 一律放行，絕不擋住正常圖。
 *
 * 逾時後底層 decode 可能還在跑，屆時 objectURL 已 revoke、decode 會晚一步 reject，
 * 但結果早已定案（fail-open 通過），無其他副作用。
 */
export const isFileDecodable = async (
  file: File,
  timeoutMs = IMAGE_DECODE_CHECK_TIMEOUT_MS
): Promise<boolean> => {
  // 第一層：位元組結構完整性。讀檔失敗是「驗證做不了」不是「圖是壞的」，
  // fail-open 交給第二層。
  try {
    if (typeof file.arrayBuffer === "function") {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!hasCompleteImageStructure(bytes)) return false;
    }
  } catch {
    // fail-open：讀不到 bytes 就跳過這層
  }

  // 第二層：既有的 decode() 驗證（對垃圾檔有效）；非瀏覽器環境一律放行。
  if (
    typeof Image === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return true;
  }
  const img = new Image();
  if (typeof img.decode !== "function") return true;
  const url = URL.createObjectURL(file);
  img.src = url;
  return verifyImageDecodable(img, timeoutMs).then((ok) => {
    URL.revokeObjectURL(url);
    return ok;
  });
};

/**
 * 把角度收斂到 [0, 360) 的整數（負角度與超過 360 都會折回；四捨五入取整）。
 * 注意：「不」吸附到 90 的倍數 —— UI 只會 +90 累加，正常流程只會出現 0/90/180/270；
 * 真的收到非 90 倍數時，吸附與否由呼叫端決定（bakeRotationIntoImage 選擇 fail-open
 * 回原檔，寧可不轉也不要輸出四角被裁的圖）。
 * 純函式、不碰 DOM，可單獨測試。
 */
export const normalizeRotation = (rotation: number): number => {
  if (!Number.isFinite(rotation)) return 0;
  return ((Math.round(rotation) % 360) + 360) % 360;
};

/** 旋轉後的顯示來源：未旋轉時是原圖 Image，旋轉後是離屏 canvas。兩者都有 width/height 且可被 drawImage 取用。 */
export type RotatedSource = HTMLImageElement | HTMLCanvasElement;

/**
 * 從「原圖 + 累計角度」重繪旋轉來源（kanban 113663）。
 *
 * 每次都從原圖重算、而不是在上一次結果上再轉：連按 4 次能精確回正、畫質不累積損失、
 * 尺寸不會錯亂。90/270 度時 canvas 寬高互換 —— 這點是「旋轉後的圖不被容器裁掉」的關鍵：
 * CSS transform 不改變 layout box，用 <img> + rotate(90deg) 會被 overflow-hidden 切掉，
 * 改用這支產生的 canvas 則能讓瀏覽器以互換後的尺寸重新 fit 進容器。
 *
 * 角度 0 直接回傳原圖（不多做一次重採樣）；取不到 2d context 時退回原圖
 * （不轉總比畫不出來好）。
 */
export const buildRotatedSource = (
  img: HTMLImageElement,
  angle: number
): RotatedSource => {
  const norm = normalizeRotation(angle);
  if (norm === 0) return img;
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement("canvas");
  const swap = norm === 90 || norm === 270;
  canvas.width = swap ? h : w;
  canvas.height = swap ? w : h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return img;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((norm * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2);
  return canvas;
};

/** bakeRotationIntoImage 的逾時保底（毫秒）。 */
export const BAKE_ROTATION_TIMEOUT_MS = 10000;

/**
 * 把檢視旋轉角度烤進圖檔像素（交卷送出前用）。
 *
 * 預覽區的旋轉鈕只是 CSS transform，送出的還是原始 bytes —— AI 拿到的稿面仍是歪的。
 * 送出前用 canvas 依角度重畫再編碼，AI 才拿得到轉正的稿面。
 *
 * - 角度正規化到 0/90/180/270；角度 0 直接回傳原檔（bytes 不變、不重編碼，避免無謂畫質損失）。
 * - 90/270 會交換 canvas 寬高；輸出 JPEG 品質與 compressImageForUpload 一致（0.92）。
 * - canvas 重畫以「顯示像素」為準（瀏覽器 drawImage 已套用 EXIF 方向），
 *   輸出檔天然不帶 EXIF orientation，不會二次旋轉。
 * - 任何失敗（載入失敗、取不到 2d context、編碼失敗）一律 fail-open 回傳原檔，
 *   絕不 throw、絕不擋住交卷。
 * - 逾時保底：少數瀏覽器遇到特定壞檔時 onload / onerror 都不會觸發，Promise 會永遠不 settle。
 *   這段跑在 navigate() 之後的背景批改裡、外層 catch 也接不到，學生只會看到「批改中」
 *   永遠不結束。所以再包一層計時器，逾時直接 fail-open 回原檔。
 *
 * @param file 原始圖片檔案
 * @param rotation 順時針角度（90 的倍數；其他值 mod 360，仍非 90 倍數時 fail-open 回原檔）
 * @param quality JPEG 品質，預設 0.92
 * @param timeoutMs 逾時毫秒數，逾時回傳原檔
 * @returns 轉正後的新 File（檔名不變）；角度 0 時為原 File 本身
 */
export const bakeRotationIntoImage = (
  file: File,
  rotation: number,
  quality = 0.92,
  timeoutMs = BAKE_ROTATION_TIMEOUT_MS
): Promise<File> => {
  const angle = normalizeRotation(rotation);
  if (angle === 0) return Promise.resolve(file);
  if (angle % 90 !== 0) {
    // 下面的 canvas 寬高只處理 0/90/180/270：拿 45 度硬畫會輸出四角被裁的圖，
    // 比不轉更糟。與整條動線一致 fail-open 回原檔，絕不 throw、不擋交卷。
    console.warn("[rotation] 非 90 倍數的角度，略過旋轉烘焙改送原檔", {
      name: file.name,
      angle,
    });
    return Promise.resolve(file);
  }

  const url = URL.createObjectURL(file);
  let revoked = false;
  const revokeOnce = () => {
    if (revoked) return;
    revoked = true;
    URL.revokeObjectURL(url);
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<File>((resolve) => {
    timer = setTimeout(() => {
      // onload/onerror 都沒回來 → 放棄旋轉，把原檔送出去，別讓批改永遠卡住
      console.warn("[rotation] 旋轉烘焙逾時，改送原檔", { name: file.name, angle });
      revokeOnce();
      resolve(file);
    }, timeoutMs);
  });

  const work = new Promise<File>((resolve) => {
    const img = new Image();
    img.onload = () => {
      revokeOnce();
      try {
        const { width, height } = img;
        const canvas = document.createElement("canvas");
        const swapSides = angle === 90 || angle === 270;
        canvas.width = swapSides ? height : width;
        canvas.height = swapSides ? width : height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -width / 2, -height / 2);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      } catch {
        // 任何 canvas 例外（例如記憶體不足）都 fail-open 回原檔，不擋交卷
        resolve(file);
      }
    };
    img.onerror = () => {
      revokeOnce();
      resolve(file);
    };
    img.src = url;
  });

  return Promise.race([work, timeout]).then((result) => {
    if (timer !== undefined) clearTimeout(timer);
    return result;
  });
};

/** collectProcessedFilesWithRotation 需要的欄位；TFile 泛型讓這支不綁 DOM 的 File，可單獨測試。 */
export interface ProcessedRotationGroup<TFile> {
  id: string;
  isProcessed?: boolean;
  processedFiles?: TFile[];
}

/**
 * 把「已校正分割完成」的 group 攤平成送出清單，同一趟帶出每個檔案的旋轉角度。
 *
 * 這支刻意複製 handleCropComplete 產生 selectedImages / croppedBlobs 的那趟展開邏輯
 * （只收 isProcessed 的 group、依序 push 它的 processedFiles），檔案與角度在同一次
 * 迭代裡配對，index 天生對齊，不需要事後比長度、也不會有「兩邊攤平規則不同」的縫。
 *
 * 順帶的效果：croppedBlobs 在「重設圖片」刪掉某個 group 時不會跟著縮，
 * 但這支只看現存且 isProcessed 的 group，被刪掉的那張自然不會被送出去。
 */
export function collectProcessedFilesWithRotation<TFile>(
  groups: ProcessedRotationGroup<TFile>[],
  rotations: Record<string, number>
): Array<{ file: TFile; angle: number }> {
  const entries: Array<{ file: TFile; angle: number }> = [];
  for (const group of groups || []) {
    if (!group || !group.isProcessed) continue;
    const angle = normalizeRotation(rotations?.[group.id] ?? 0);
    for (const file of group.processedFiles ?? []) entries.push({ file, angle });
  }
  return entries;
}

/** collectOriginSourcesWithRotation 需要的欄位；TFile 泛型讓這支不綁 DOM 的 File，可單獨測試。 */
export interface OriginRotationGroup<TFile> {
  id: string;
  correctedUrl?: string;
  originalFile: TFile;
}

/** collectOriginSourcesWithRotation 的輸出：一個 group 一格，來源與角度已配好。 */
export interface OriginSourceEntry<TFile> {
  id: string;
  correctedUrl?: string;
  originalFile: TFile;
  angle: number;
}

/**
 * 把 originFiles（送給後端的「整頁原稿」）的來源與旋轉角度配對起來。
 *
 * originFiles 與 files 是兩份不同的東西：files 是分割後的小圖、給 AI 批改；
 * originFiles 是整頁圖，後端存進 studentInputOriginUrl，而 Feedback 頁、匯出 PDF、
 * 班級成績單 ZIP 三處顯示時都優先用它。先前這條路徑獨立組出、完全沒經過
 * bakeRotationIntoImage，症狀是「AI 收到轉正的稿子，老師和學生看到的還是歪的」。
 *
 * 這支只做純邏輯的「挑來源 + 配角度」，抓檔（urlToFile）與烤旋轉留在呼叫端，
 * 因為那兩件事要碰 DOM／網路，抽開才測得動。
 *
 * - 一個 group 一格，順序照 groups；null group 整格略過，不推移後面的角度。
 * - correctedUrl 原樣帶出（可能為 undefined）；originalFile 一律帶出，供呼叫端在
 *   urlToFile 失敗時 fail-open 退回原檔。
 * - 角度查不到視為 0，一律走 normalizeRotation。
 *
 * 註：走過四角校正的 group，旋轉已烘進校正輸出、預覽區角度會被歸零
 * （見 UploadEssay handleCorrectionComplete），所以它們的 angle 通常是 0、套用等同 no-op；
 * 真正需要烤的是沒有校正輸出、原圖直送的那條路徑。
 */
export function collectOriginSourcesWithRotation<TFile>(
  groups: OriginRotationGroup<TFile>[],
  rotations: Record<string, number>
): Array<OriginSourceEntry<TFile>> {
  const entries: Array<OriginSourceEntry<TFile>> = [];
  for (const group of groups || []) {
    if (!group) continue;
    entries.push({
      id: group.id,
      correctedUrl: group.correctedUrl,
      originalFile: group.originalFile,
      angle: normalizeRotation(rotations?.[group.id] ?? 0),
    });
  }
  return entries;
}

/**
 * 交卷分支 1（skipCorrect 原圖直送）與分支 3（尚未分割、走 autoOptimizeImage）的
 * 送出清單：從 processedGroups 直接展開「一個 group 一個檔（originalFile）+ 同 group 的角度」。
 *
 * 之前這兩條吃的是 selectedImages 陣列、再按 index 配角度。但「重設圖片」
 * （removeProcessedGroup）只從 processedGroups 拿掉一格，selectedImages 要到歸零才清 ——
 * 刪 1 張後兩邊長度對不上，長度守門一觸發整批不轉，而已刪掉的那張還照樣被送出去。
 * 改成從 groups 展開後，檔案與角度同 key 天生同步，被刪的 group 自然消失。
 *
 * 刻意委派給 collectOriginSourcesWithRotation、不另寫一套攤平：files（給 AI 批改）與
 * originFiles（Feedback/PDF/ZIP 顯示）是同一批 group 的兩份清單，共用同一趟展開規則，
 * 同一個 group 在兩邊拿到的角度就不可能分岔。
 */
export function collectSubmitOriginalsWithRotation<TFile>(
  groups: OriginRotationGroup<TFile>[],
  rotations: Record<string, number>
): Array<{ file: TFile; angle: number }> {
  return collectOriginSourcesWithRotation(groups, rotations).map((entry) => ({
    file: entry.originalFile,
    angle: entry.angle,
  }));
}
